import React from 'react'
import PropTypes from 'prop-types'
import { ButtonGroup, Highlight } from '@/component/Primitive/Events'
import styles from './EventSteps.module.scss'
import cx from 'classnames'
import Container from '@/component/Primitive/Container'
import Stack from '@/component/Primitive/Stack'
import Type from '@/component/Primitive/Type'
import Prose from '@/component/Primitive/Prose'

export const EventSteps = ({ title, description, theme, steps }) => {
  return (
    <div
      className={cx(
        styles.EventSteps,
        theme === 'dark' && styles.dark,
        theme === 'grey' && styles.grey
      )}
    >
      <Container center size="wide" gutter>
        {(title || description) && (
          <div className={styles.Header}>
            {title && (
              <Type
                as="h2"
                size={['heading5', 'heading2']}
                weight="medium"
                className={styles.Title}
              >
                <Highlight classNames={{ strong: styles.Highlight }}>
                  {title}
                </Highlight>
              </Type>
            )}
            {description && (
              <Type size={['body2', 'body2']}>
                <Prose
                  className={styles.Description}
                  dangerousHtml={description}
                />
              </Type>
            )}
          </div>
        )}

        {steps && steps.length > 0 && (
          <div className={styles.StepsList}>
            {[...steps]
              .sort((a, b) => (a.position || 0) - (b.position || 0))
              .map((step, index) => (
                <Step key={index} {...step} />
              ))}
          </div>
        )}
      </Container>
    </div>
  )
}

const Step = ({
  positionLabel,
  title,
  description,
  theme = 'light',
  buttonGroup
}) => {
  const hasPosition = !!positionLabel

  return (
    <div className={cx(styles.Step, styles[theme])}>
      {hasPosition && (
        <div className={styles.PositionColumn}>
          <Type as="div" className={styles.Position}>
            {positionLabel}
          </Type>
        </div>
      )}
      <div
        className={cx(styles.ContentColumn, !hasPosition && styles.fullWidth)}
      >
        <Stack gap="small">
          {title && (
            <Type
              as="h3"
              size={['heading6', 'heading4']}
              weight="medium"
              className={styles.StepTitle}
            >
              <Highlight classNames={{ strong: styles.Highlight }}>
                {title}
              </Highlight>
            </Type>
          )}
          {description && (
            <Type size={['body2', 'body2']}>
              <Prose
                className={styles.StepDescription}
                dangerousHtml={description}
              />
            </Type>
          )}
          {buttonGroup && buttonGroup.length > 0 && (
            <div className={styles.ButtonGroup}>
              <ButtonGroup
                group={buttonGroup}
                theme={theme}
                classNames={{ button: styles.Button }}
              />
            </div>
          )}
        </Stack>
      </div>
      {hasPosition && <div className={styles.EmptyColumn} />}
    </div>
  )
}

EventSteps.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  theme: PropTypes.string,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.number,
      positionLabel: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      theme: PropTypes.string,
      buttonGroup: PropTypes.array
    })
  )
}

Step.propTypes = {
  positionLabel: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  theme: PropTypes.string,
  buttonGroup: PropTypes.array
}

export default EventSteps
